import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { COUNTRIES } from '@/lib/countries';
export function countryByIso(iso) {
    return COUNTRIES.find((c) => c.iso === iso) ?? COUNTRIES[0];
}
/** Strip everything except digits. */
export function digitsOnly(v) {
    return (v || '').replace(/\D/g, '');
}
/** Combined E.164-ish value for storage, e.g. "+1 5551234567". Empty if no number. */
export function combinePhone(iso, number) {
    const digits = digitsOnly(number);
    if (!digits)
        return '';
    const c = countryByIso(iso);
    const dial = c.dial === '+' ? '+' : c.dial;
    return `${dial} ${digits}`.trim();
}
/**
 * Validate a national number against the selected country's expected digit
 * length. Returns an error string, or null when acceptable (or empty + optional).
 */
export function validateNationalNumber(iso, number, opts = {}) {
    const { required = false } = opts;
    const digits = digitsOnly(number);
    if (!digits)
        return required ? 'Phone number is required.' : null;
    const c = countryByIso(iso);
    if (digits.length < c.min || digits.length > c.max) {
        const range = c.min === c.max ? `${c.min} digits` : `${c.min}–${c.max} digits`;
        return `${c.name} numbers are ${range} (you entered ${digits.length}).`;
    }
    return null;
}
const labelCls = 'text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]';
/**
 * Phone input: country-code dropdown (left) + digits-only number entry (right),
 * with length validation driven by the selected country. (QA: Udhay)
 */
export function PhoneField({ label, country, number, onCountryChange, onNumberChange, onBlur, error, required, hint, }) {
    const c = countryByIso(country);
    const borderCls = error
        ? 'border-[var(--rose-200)] focus-within:border-[var(--rose-200)] focus-within:ring-[var(--rose-100)]'
        : 'border-[var(--line)] focus-within:border-[var(--lav-200)] focus-within:ring-[var(--lav-100)]';
    return (_jsxs("label", { className: "block space-y-1.5", children: [_jsxs("div", { className: "flex items-baseline justify-between", children: [_jsxs("span", { className: labelCls, style: { fontFamily: 'var(--f-mono)' }, children: [label, required && _jsx("span", { className: "text-[var(--crimson)]", "aria-hidden": "true", children: " *" })] }), hint] }), _jsxs("div", { className: `flex items-stretch overflow-hidden rounded-[var(--r-md)] border bg-white transition-[box-shadow,border-color] focus-within:outline-none focus-within:ring-2 ${borderCls}`, children: [_jsx("select", { "aria-label": "Country code", value: country, onChange: (e) => onCountryChange(e.target.value), onBlur: onBlur, className: "shrink-0 border-0 bg-transparent py-2.5 pl-3 pr-2 text-[14px] text-[var(--ink-900)] focus:outline-none", children: COUNTRIES.map((opt) => (_jsxs("option", { value: opt.iso, children: [opt.flag, " ", opt.dial === '+' ? 'Other' : opt.dial] }, opt.iso))) }), _jsx("span", { className: "my-2 w-px bg-[var(--line)]", "aria-hidden": true }), _jsx("input", { type: "tel", inputMode: "numeric", autoComplete: "tel-national", value: number, "aria-invalid": !!error, placeholder: c.min === c.max ? `${c.min}-digit number` : `${c.min}–${c.max} digits`, onChange: (e) => onNumberChange(digitsOnly(e.target.value)), onBlur: onBlur, className: "min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-[14px] text-[var(--ink-900)] focus:outline-none" })] }), error && _jsx("span", { className: "block text-[12px] text-[var(--crimson)]", children: error })] }));
}
